import React, { useEffect, useMemo, useState } from "react";
import {
  Copy,
  RefreshCw,
  UserPlus,
  Shield,
  Trash2,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { apiFetch } from "../apiFetch"; // adjust path if needed
import { useAuth } from "../context/AuthContext";
import { getApiBase } from "../config"; // adjust if needed

type Role = "OWNER" | "ADMIN" | "MANAGER" | "USER";
type InviteRole = "ADMIN" | "MANAGER" | "USER";

type OrgUser = {
  id: string;
  full_name: string;
  email: string;
  role: Role;
  is_active: boolean;
  created_at?: string | null;
  last_login_at?: string | null; // if your API returns last_login instead, map it below
};

type Invite = {
  id: string;
  email: string;
  full_name?: string | null;
  role: InviteRole;
  status: "PENDING" | "ACCEPTED" | "REVOKED";
  is_expired: boolean;
  expires_at: string;
  created_at: string;
  invited_by?: { id: string; full_name?: string | null; email?: string | null };
};

type DirectoryResponse = {
  users: OrgUser[];
  invites: Invite[];
  meta: {
    org_id: string;
    counts?: {
      active_users: number;
      inactive_users: number;
      pending_invites: number;
    };
  };
};

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function formatDt(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function isPrivileged(role?: string | null) {
  return role === "OWNER" || role === "ADMIN";
}

const rolePillClass = (role: Role | InviteRole) => {
  // keep it subtle but readable in both modes
  const base =
    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1";
  switch (role) {
    case "OWNER":
      return `${base} bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:ring-amber-800/40`;
    case "ADMIN":
      return `${base} bg-indigo-50 text-indigo-800 ring-indigo-200 dark:bg-indigo-950/30 dark:text-indigo-200 dark:ring-indigo-800/40`;
    case "MANAGER":
      return `${base} bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200 dark:ring-emerald-800/40`;
    default:
      return `${base} bg-slate-100 text-slate-800 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-200 dark:ring-slate-700`;
  }
};

function Button({
  children,
  onClick,
  disabled,
  variant = "primary",
  title,
  className,
  type = "button",
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: "primary" | "ghost" | "danger";
  title?: string;
  className?: string;
  type?: "button" | "submit";
}) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition " +
    "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed";
  const styles =
    variant === "primary"
      ? "bg-slate-900 text-white hover:bg-slate-800 focus:ring-slate-400 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
      : variant === "danger"
        ? "bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-400 dark:bg-rose-500 dark:hover:bg-rose-600"
        : "bg-transparent text-slate-700 hover:bg-slate-100 focus:ring-slate-300 dark:text-slate-200 dark:hover:bg-slate-800";
  return (
    <button
      type={type}
      className={cx(base, styles, className)}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  type = "text",
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  className?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cx(
        "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none",
        "focus:border-slate-400 focus:ring-2 focus:ring-slate-200",
        "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-slate-500 dark:focus:ring-slate-800",
        className,
      )}
    />
  );
}

function Select({
  value,
  onChange,
  options,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cx(
        "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none",
        "focus:border-slate-400 focus:ring-2 focus:ring-slate-200",
        "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-slate-500 dark:focus:ring-slate-800",
        className,
      )}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Modal({
  open,
  title,
  children,
  onClose,
}: {
  open: boolean;
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        className="absolute inset-0 bg-black/30 dark:bg-black/50"
        onClick={onClose}
        aria-label="Close modal backdrop"
      />
      <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-800 dark:bg-slate-950">
        <div className="mb-4 flex items-center justify-between">
          <div className="text-base font-bold text-slate-900 dark:text-slate-100">
            {title}
          </div>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function SettingsUsersPage() {
  const { role, accessToken, isAuthenticated, logout } = useAuth();
  // must provide current user incl. role
  const canManage = isPrivileged(role ?? null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [directory, setDirectory] = useState<DirectoryResponse | null>(null);

  // Invite modal state
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState<InviteRole>("USER");
  const [inviteResultLink, setInviteResultLink] = useState<string | null>(null);

  // UI filters
  const [query, setQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const apiBase = getApiBase();

  async function loadDirectory() {
    setLoading(true);
    setErr(null);
    try {
      const res = await apiFetch(`${apiBase}/org/users`, { method: "GET" });
      // If your backend returns last_login (not last_login_at), normalize here:
      const normalized: DirectoryResponse = {
        ...res,
        users: (res.users ?? []).map((u: any) => ({
          ...u,
          last_login_at: u.last_login_at ?? u.last_login ?? null,
        })),
      };
      setDirectory(normalized);
    } catch (e: any) {
      setErr(e?.message || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDirectory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(() => {
    const users = directory?.users ?? [];
    const q = query.trim().toLowerCase();
    return users
      .filter((u) => (showInactive ? true : u.is_active))
      .filter((u) => {
        if (!q) return true;
        return (
          u.full_name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          u.role.toLowerCase().includes(q)
        );
      });
  }, [directory, query, showInactive]);

  const filteredInvites = useMemo(() => {
    const invites = directory?.invites ?? [];
    const q = query.trim().toLowerCase();
    return invites.filter((i) => {
      if (!q) return true;
      return (
        (i.full_name ?? "").toLowerCase().includes(q) ||
        i.email.toLowerCase().includes(q) ||
        i.role.toLowerCase().includes(q) ||
        (i.is_expired ? "expired" : "pending").includes(q)
      );
    });
  }, [directory, query]);

  async function handleInvite() {
    setBusy(true);
    setErr(null);
    setInviteResultLink(null);
    try {
      const payload = {
        email: inviteEmail.trim(),
        full_name: inviteName.trim() || null,
        role: inviteRole,
      };
      const res = await apiFetch(`${apiBase}/org/users/invite`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
      });
      setInviteResultLink(res.invite_link ?? null);

      // Refresh list so invite appears immediately
      await loadDirectory();
    } catch (e: any) {
      setErr(e?.message || "Invite failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleResend(inviteId: string) {
    setBusy(true);
    setErr(null);
    setInviteResultLink(null);
    try {
      const res = await apiFetch(
        `${apiBase}/org/users/invite/${inviteId}/resend`,
        {
          method: "POST",
        },
      );
      setInviteResultLink(res.invite_link ?? null);
      await loadDirectory();
    } catch (e: any) {
      setErr(e?.message || "Resend failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleActive(u: OrgUser) {
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`${apiBase}/org/users/${u.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: !u.is_active }),
      });
      await loadDirectory();
    } catch (e: any) {
      setErr(e?.message || "Update failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRoleChange(u: OrgUser, newRole: InviteRole) {
    if (u.role === "OWNER") return;
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`${apiBase}/org/users/${u.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: newRole }),
      });
      await loadDirectory();
    } catch (e: any) {
      setErr(e?.message || "Role update failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(u: OrgUser) {
    const ok = window.confirm(
      `Remove ${u.full_name} (${u.email})?\n\nThis will deactivate access.`,
    );
    if (!ok) return;
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`${apiBase}/org/users/${u.id}`, { method: "DELETE" });
      await loadDirectory();
    } catch (e: any) {
      setErr(e?.message || "Remove failed.");
    } finally {
      setBusy(false);
    }
  }

  async function copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
  }

  if (!canManage) {
    return (
      <div className="p-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-900 shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100">
          <div className="text-lg font-bold">Access restricted</div>
          <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Only OWNER or ADMIN can manage users.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-xl font-extrabold text-slate-900 dark:text-slate-100">
            User Management
          </div>
          <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            Invite teammates, manage roles, and revoke access.
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            onClick={loadDirectory}
            disabled={loading || busy}
            title="Refresh list"
          >
            <RefreshCw size={16} />
            Refresh
          </Button>

          <Button onClick={() => setInviteOpen(true)} disabled={busy}>
            <UserPlus size={16} />
            Invite user
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="mb-4 grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <TextInput
            value={query}
            onChange={setQuery}
            placeholder="Search name, email, role…"
          />
        </div>

        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-900">
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Show inactive
          </div>
          <button
            onClick={() => setShowInactive((v) => !v)}
            className="rounded-full p-1 hover:bg-slate-200 dark:hover:bg-slate-800"
            aria-label="Toggle show inactive"
          >
            {showInactive ? (
              <ToggleRight className="text-slate-900 dark:text-slate-100" />
            ) : (
              <ToggleLeft className="text-slate-500" />
            )}
          </button>
        </div>
      </div>

      {/* Error */}
      {err && (
        <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
          {err}
        </div>
      )}

      {/* Invite link output (handy for Phase-1 delivery)
      {inviteResultLink && (
        <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-bold text-slate-900 dark:text-slate-100">
                Invite link
              </div>
              <div className="mt-1 break-all text-xs text-slate-600 dark:text-slate-300">
                {inviteResultLink}
              </div>
            </div>
            <Button variant="ghost" onClick={() => copyLink(inviteResultLink)}>
              <Copy size={16} />
              Copy
            </Button>
          </div>
        </div>
      )} */}

      {/* Content */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Users */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
          <div className="flex items-center justify-between border-b border-slate-200 p-4 dark:border-slate-800">
            <div className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
              Users
            </div>
            <div className="text-xs text-slate-600 dark:text-slate-300">
              {directory?.meta?.counts
                ? `${directory.meta.counts.active_users} active • ${directory.meta.counts.inactive_users} inactive`
                : `${filteredUsers.length} shown`}
            </div>
          </div>

          <div className="divide-y divide-slate-100 dark:divide-slate-900">
            {loading ? (
              <div className="p-4 text-sm text-slate-600 dark:text-slate-300">
                Loading…
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-4 text-sm text-slate-600 dark:text-slate-300">
                No users found.
              </div>
            ) : (
              filteredUsers.map((u) => {
                const canEditRole =
                  u.role !== "OWNER" &&
                  (role === "OWNER" || u.role !== "ADMIN"); // ADMIN can’t change ADMIN/OWNER
                const canDeactivate = u.role !== "OWNER";

                return (
                  <div key={u.id} className="p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="truncate text-sm font-bold text-slate-900 dark:text-slate-100">
                            {u.full_name}
                          </div>
                          <span className={rolePillClass(u.role)}>
                            <Shield size={12} />
                            {u.role}
                          </span>
                          {!u.is_active && (
                            <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="mt-1 truncate text-xs text-slate-600 dark:text-slate-300">
                          {u.email}
                        </div>
                        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                          Last login: {formatDt(u.last_login_at)} • Created:{" "}
                          {formatDt(u.created_at)}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {canEditRole ? (
                          <div className="w-44">
                            <Select
                              value={u.role}
                              onChange={(v) =>
                                handleRoleChange(u, v as InviteRole)
                              }
                              options={[
                                { value: "USER", label: "USER" },
                                { value: "MANAGER", label: "MANAGER" },
                                ...(role === "OWNER"
                                  ? [{ value: "ADMIN", label: "ADMIN" }]
                                  : []),
                              ]}
                            />
                          </div>
                        ) : (
                          <div className="w-44">
                            <Select
                              value={u.role}
                              onChange={() => {}}
                              options={[{ value: u.role, label: u.role }]}
                              className="opacity-70"
                            />
                          </div>
                        )}

                        <Button
                          variant="ghost"
                          disabled={busy || !canDeactivate}
                          onClick={() => handleToggleActive(u)}
                          title={
                            !canDeactivate
                              ? "You can’t deactivate this user."
                              : u.is_active
                                ? "Deactivate"
                                : "Reactivate"
                          }
                        >
                          {u.is_active ? (
                            <ToggleRight size={16} />
                          ) : (
                            <ToggleLeft size={16} />
                          )}
                          {u.is_active ? "Active”" : "Inactive"}
                        </Button>

                        <Button
                          variant="danger"
                          disabled={busy || u.role === "OWNER"}
                          onClick={() => handleRemove(u)}
                          title="Remove (soft)"
                        >
                          <Trash2 size={16} />
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Invites */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
          <div className="flex items-center justify-between border-b border-slate-200 p-4 dark:border-slate-800">
            <div className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
              Pending invites
            </div>
            <div className="text-xs text-slate-600 dark:text-slate-300">
              {directory?.meta?.counts
                ? `${directory.meta.counts.pending_invites} pending`
                : `${filteredInvites.length} shown`}
            </div>
          </div>

          <div className="divide-y divide-slate-100 dark:divide-slate-900">
            {loading ? (
              <div className="p-4 text-sm text-slate-600 dark:text-slate-300">
                Loading…
              </div>
            ) : filteredInvites.length === 0 ? (
              <div className="p-4 text-sm text-slate-600 dark:text-slate-300">
                No pending invites.
              </div>
            ) : (
              filteredInvites.map((inv) => (
                <div key={inv.id} className="p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="truncate text-sm font-bold text-slate-900 dark:text-slate-100">
                          {inv.full_name || "—"}
                        </div>
                        <span className={rolePillClass(inv.role)}>
                          <Shield size={12} />
                          {inv.role}
                        </span>

                        <span
                          className={cx(
                            "inline-flex rounded-full px-2 py-0.5 text-xs font-semibold",
                            inv.is_expired
                              ? "bg-rose-50 text-rose-800 dark:bg-rose-950/30 dark:text-rose-200"
                              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
                          )}
                        >
                          {inv.is_expired ? "Expired" : "Valid"}
                        </span>
                      </div>

                      <div className="mt-1 truncate text-xs text-slate-600 dark:text-slate-300">
                        {inv.email}
                      </div>
                      <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                        Expires: {formatDt(inv.expires_at)} • Invited:{" "}
                        {formatDt(inv.created_at)}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="ghost"
                        disabled={busy}
                        onClick={() => handleResend(inv.id)}
                        title="Resend invite (rotates token)"
                      >
                        <RefreshCw size={16} />
                        Resend
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Invite modal */}
      <Modal
        open={inviteOpen}
        title="Invite a user"
        onClose={() => setInviteOpen(false)}
      >
        <div className="grid gap-3">
          <div>
            <div className="mb-1 text-xs font-bold text-slate-700 dark:text-slate-300">
              Email
            </div>
            <TextInput
              value={inviteEmail}
              onChange={setInviteEmail}
              placeholder="name@company.com"
              type="email"
            />
          </div>

          <div>
            <div className="mb-1 text-xs font-bold text-slate-700 dark:text-slate-300">
              Full name (optional)
            </div>
            <TextInput
              value={inviteName}
              onChange={setInviteName}
              placeholder="Jane Doe"
            />
          </div>

          <div>
            <div className="mb-1 text-xs font-bold text-slate-700 dark:text-slate-300">
              Role
            </div>
            <Select
              value={inviteRole}
              onChange={(v) => setInviteRole(v as InviteRole)}
              options={[
                { value: "USER", label: "USER" },
                { value: "MANAGER", label: "MANAGER" },
                ...(role === "OWNER"
                  ? [{ value: "ADMIN", label: "ADMIN" }]
                  : []),
              ]}
            />
            <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              ADMIN can manage users. MANAGER/USER have standard access.
            </div>
          </div>

          <div className="mt-2 flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setInviteOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={busy || !inviteEmail.trim()}
              title={!inviteEmail.trim() ? "Enter an email" : "Create invite"}
            >
              <UserPlus size={16} />
              Create invite
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
